import BatteryUnknownOutlined from '@mui/icons-material/BatteryUnknownOutlined';
import ThumbUpOutlined from '@mui/icons-material/ThumbUpOutlined';
import { useTheme } from '@mui/material';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import BatteryAndroid0Outlined from 'v2/assets/vector/lockIcons/BatteryAndroid0Outlined';
import BatteryAndroidFrame1Outlined from 'v2/assets/vector/lockIcons/BatteryAndroidFrame1Outlined';
import BatteryAndroidFrame3Outlined from 'v2/assets/vector/lockIcons/BatteryAndroidFrame3Outlined';
import { filterBatteryStatusDefault } from 'v2/filters/devices/deviceFilterConstants';
import { serializeBooleanRecordToParam } from 'v2/filters/filterUrlParams';
import { RowsProps } from 'v2/pages/Dashboard/cards/Card';
import { getSeverityColors } from 'v2/pages/Dashboard/hooks/getSeverityColors';
import { DEVICES_OVERVIEW_PATH } from 'v2/paths';
import { formatCountLabel } from 'v2/utils/formatCountLabel';

interface Props {
  batteriesEmpty: number;
  batteriesCritical: number;
  batteriesLow: number;
  batteriesUnknown: number;
}

type BatteryStatus = 'empty' | 'critical' | 'low' | 'unknown';

type BatteryRow = Pick<RowsProps, 'Icon' | 'text'> & {
  count: number;
  severity: 'error' | 'warning' | 'secondary';
  status: BatteryStatus;
  onClick: () => void;
};

const BATTERY_ORDER: Record<BatteryStatus, number> = {
  empty: 0,
  critical: 1,
  low: 2,
  unknown: 3
};

const useBatteryRows = ({ batteriesEmpty, batteriesCritical, batteriesLow, batteriesUnknown }: Props): RowsProps[] => {
  const { translation } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  return useMemo(() => {
    const severityColors = getSeverityColors(theme);

    const goToDevicesWithBatteryStatus = (status: keyof typeof filterBatteryStatusDefault) => {
      const batteryStatus = serializeBooleanRecordToParam({ ...filterBatteryStatusDefault, [status]: true });
      navigate(`${DEVICES_OVERVIEW_PATH}?batteryStatus=${batteryStatus}`);
    };

    const rows: BatteryRow[] = [
      {
        count: batteriesEmpty,
        text: formatCountLabel(
          batteriesEmpty,
          translation.dashboard_batteries_empty_singular,
          translation.dashboard_batteries_empty_plural
        ),
        Icon: BatteryAndroid0Outlined,
        severity: 'error',
        status: 'empty',
        onClick: () => goToDevicesWithBatteryStatus('empty')
      },
      {
        count: batteriesCritical,
        text: formatCountLabel(
          batteriesCritical,
          translation.dashboard_batteries_critical_singular,
          translation.dashboard_batteries_critical_plural
        ),
        Icon: BatteryAndroidFrame1Outlined,
        severity: 'error',
        status: 'critical',
        onClick: () => goToDevicesWithBatteryStatus('critical')
      },
      {
        count: batteriesLow,
        text: formatCountLabel(
          batteriesLow,
          translation.dashboard_batteries_low_singular,
          translation.dashboard_batteries_low_plural
        ),
        Icon: BatteryAndroidFrame3Outlined,
        severity: 'warning',
        status: 'low',
        onClick: () => goToDevicesWithBatteryStatus('low')
      },
      {
        count: batteriesUnknown,
        text: formatCountLabel(
          batteriesUnknown,
          translation.dashboard_batteries_unknown_singular,
          translation.dashboard_batteries_unknown_plural
        ),
        Icon: BatteryUnknownOutlined,
        severity: 'secondary',
        status: 'unknown',
        onClick: () => goToDevicesWithBatteryStatus('unknown')
      }
    ];

    const sortedRows = rows
      .filter((row) => row.count > 0)
      .sort((a, b) => BATTERY_ORDER[a.status] - BATTERY_ORDER[b.status]);

    if (sortedRows.length === 0) {
      return [
        {
          Icon: ThumbUpOutlined,
          text: translation.all_batteries_are_good,
          onClick: () => navigate(DEVICES_OVERVIEW_PATH),
          ...severityColors.success
        }
      ];
    }

    return sortedRows.map((row) => ({
      Icon: row.Icon,
      text: row.text,
      onClick: row.onClick,
      ...severityColors[row.severity],
      ...(row.status === 'unknown' && {
        textColor: theme.palette.text.primary,
        iconColor: theme.custom.action.active
      })
    }));
  }, [batteriesEmpty, batteriesCritical, batteriesLow, batteriesUnknown, translation, theme, navigate]);
};

export default useBatteryRows;
